import clsx from "clsx";
import {
  ChartLine,
  CircleHelp,
  MessageSquare,
  PencilLine,
  ShoppingCart,
  Tag,
  TrendingUp,
  Truck,
  Users,
} from "lucide-react";
import React from "react";

import { IgTextField } from "@intelligems/ig-theme/IgTextField";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { OnboardingInfoFe } from "@intelligems/ig-types/fe-only/onboarding";
import { ListItemButton, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import { getMixpanelStepTitle } from "@src/components/MainApp/Onboarding/utils";
import { useViewPage } from "@src/hooks/mixpanel";

interface OnboardingGoalsInfoProps {
  data: Pick<OnboardingInfoFe, "otherGoal" | "goals">;
  onChange: (
    field: keyof OnboardingInfoFe,
    value: string | string[] | null | undefined
  ) => void;
}

const PREFIX = "OnboardingGoalsInfo";

const classes = {
  root: `${PREFIX}-root`,
  selected: `${PREFIX}-selected`,
  disabled: `${PREFIX}-disabled`,
};

const StyledStack = styled(Stack)(({ theme }) => ({
  [`& .${classes.root}`]: {
    borderRadius: "8px",
    // alignContent: "center",
    padding: theme.spacing(6),
    border: `2px solid ${theme.igBorders.core.default}`,
    "&:hover, &:focus": {
      border: `2px solid ${theme.igBorders.accent.hover}`,
    },
  },
  [`& .${classes.disabled}`]: {
    borderRadius: "8px",
    opacity: 0.5,
    // alignContent: "center",
    padding: theme.spacing(6),
    border: `2px solid ${theme.igBorders.core.default}`,
    "&:hover, &:focus": {
      border: `2px solid ${theme.igBorders.accent.hover}`,
    },
  },
  [`& .${classes.selected}`]: {
    borderRadius: "8px",
    backgroundColor: `${theme.igBackgroundColors.accent} !important`,
    // alignContent: "center",
    padding: theme.spacing(6),
    border: `2px solid ${theme.igBorders.accent.default}`,
  },
}));

export const OnboardingGoalsInfo = ({
  data,
  onChange,
}: OnboardingGoalsInfoProps) => {
  const theme = useTheme();
  const goals = [
    {
      title: "Boost Profit Margins",
      description:
        "Discover which offers and strategies drive the most profit.",
      icon: <ChartLine />,
    },
    {
      title: "Build Smarter Offers",
      description:
        "Launch margin-preserving offers that convert—no dev work needed.",
      icon: <ShoppingCart />,
    },
    {
      title: "Test Site Messaging",
      description: "Experiment with copy and design to increase conversions.",
      icon: <MessageSquare />,
    },
    {
      title: "Optimize Product Pricing",
      description:
        "Use data to find price points that balance sales and margin.",
      icon: <Tag />,
    },
    {
      title: "Increase Conversions",
      description: "Turn more site visitors into buyers with better tactics.",
      icon: <TrendingUp />,
    },
    {
      title: "Personalize the Experience",
      description: "Show content tailored to real-time shopper behavior.",
      icon: <Users />,
    },
    {
      title: "Tweak Shipping Strategy",
      description: "Adjust rates and thresholds to boost checkout success.",
      icon: <Truck />,
    },
    {
      title: "Help Me Choose",
      description: "Not sure where to start? We'll highlight quick wins.",
      icon: <CircleHelp />,
    },
    {
      title: "Set a Custom Goal",
      description: "Have your own success target? Let's make it happen.",
      icon: <PencilLine />,
    },
  ];

  const handleGoalClick = (value: string) => {
    let newGoals: string[] = [...data.goals];
    if (value === "Help Me Choose") {
      newGoals = ["Help Me Choose"];
    } else if (value === "Set a Custom Goal") {
      if (data.goals.includes("Set a Custom Goal")) {
        newGoals = newGoals.filter((g) => g !== value);
      } else {
        newGoals = ["Set a Custom Goal"];
      }
    } else if (
      data.goals.includes("Help Me Choose") ||
      data.goals.includes("Set a Custom Goal")
    ) {
      newGoals = [value];
    } else if (data.goals.includes(value)) {
      newGoals = newGoals.filter((g) => g !== value);
    } else if (data.goals.length < 3) {
      newGoals.push(value);
    }

    if (data.otherGoal && !newGoals.includes("Set a Custom Goal")) {
      onChange("otherGoal", "");
    }
    onChange("goals", newGoals);
  };

  const isDisabled = (value: string) => {
    if (data.goals.includes("Help Me Choose")) {
      return value !== "Help Me Choose";
    }
    if (data.goals.includes("Set a Custom Goal")) {
      return value !== "Set a Custom Goal";
    }
    return value === "Help Me Choose" && data.goals.length > 0;
  };

  useViewPage(`Onboarding - ${getMixpanelStepTitle(2)}`);
  return (
    <StyledStack spacing={6} width={"100%"} direction={"column"}>
      <Stack direction={"column"} spacing={2}>
        <IgTypography variant={"h1"} fontWeight={"600"}>
          Your goals with Intelligems
        </IgTypography>
        <IgTypography color={theme.palette.text.secondary}>
          What are your goals with using Intelligems? Select up to 3 options
          that best align with your objectives.
        </IgTypography>
      </Stack>
      <Grid container direction={"row"} spacing={4}>
        {goals.map((goal) => (
          <Grid
            key={goal.title}
            size={{
              xs: 12,
              sm: 6,
              md: 4,
            }}
          >
            <ListItemButton
              sx={{ height: "100%" }}
              key={goal.title}
              selected={data.goals.includes(goal.title)}
              onClick={() => handleGoalClick(goal.title)}
              classes={{
                root: clsx({
                  [classes.root]: !isDisabled(goal.title),
                  [classes.disabled]: isDisabled(goal.title),
                }),
                selected: classes.selected,
              }}
            >
              <Stack
                alignContent={"start"}
                alignItems={"center"}
                textAlign={"center"}
                // display={"flex"}
                direction={"column"}
                spacing={2}
                sx={{ height: "100%" }}
              >
                <Grid
                  container
                  alignItems={"center"}
                  sx={{
                    backgroundColor:
                      theme.igBackgroundColors.interactive.neutral.default,
                    padding: "12px",
                    width: "48px",
                    height: "48px",
                    borderRadius: 2,
                  }}
                >
                  {goal.icon}
                </Grid>
                <IgTypography variant={"h3"}>{goal.title}</IgTypography>
                <IgTypography color={theme.palette.text.secondary}>
                  {goal.description}
                </IgTypography>
              </Stack>
            </ListItemButton>
          </Grid>
        ))}
      </Grid>
      {data.goals.includes("Set a Custom Goal") && (
        <IgTextField
          label={"Tell us about your custom goal"}
          value={data.otherGoal || ""}
          onChange={(e) => onChange("otherGoal", e.target.value)}
          fullWidth={true}
          slotProps={{
            htmlInput: {
              maxLength: 100,
            },
          }}
          placeholder="Describe what you'd like to achieve with Intelligems..."
          required
        />
      )}
    </StyledStack>
  );
};
